"use client";

import type { ReactNode } from "react";
import { useMemo, useState } from "react";
import dynamic from "next/dynamic";
import {
  Box as MantineBox,
  Select,
  Table as MantineTable,
  Text as MantineText,
  Title as MantineTitle,
  UnstyledButton as MantineUnstyledButton,
} from "@mantine/core";
import {
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";

import styles from "../styles.module.scss";
import {
  BUBBLE_COLORS,
  BubbleTooltip,
  PriceVendorTable,
  VendorTable,
  ViolinChart,
} from "../lib/reportComponents";
import {
  countryName,
  formatNumber,
  getArbitrageData,
  getViolinData,
  type ArbitrageWindowsData,
  type BiggestMovementsData,
  type HighestQuantityData,
  type LowestMedianData,
  type MedianPriceData,
  type MovementSegment,
  type MovementView,
  type PriceDistributionData,
  type ReportMapData,
  type TopVendorsData,
  type VendorBubbleData,
} from "../lib/reportData";

const AnalyticsMap = dynamic(() => import("../AnalyticsMap"), { ssr: false });

const EMPTY_VENDOR_GROUPS = { marketplace: [], franchise: [], independent: [] };

function getBrands(brands: string[], initialBrand?: string) {
  const sorted = [...brands].sort((a, b) => a.localeCompare(b));
  return initialBrand && !sorted.includes(initialBrand) ? [initialBrand, ...sorted] : sorted;
}

export function BiggestMovementsSection({
  data,
  subtitle,
  note,
}: {
  data: BiggestMovementsData;
  subtitle: string;
  note: ReactNode;
}) {
  const [view, setView] = useState<MovementView>("price");
  const [segment, setSegment] = useState<MovementSegment>("clothing");
  const isSegmented = "segments" in data;

  const columns = useMemo(() => {
    if (isSegmented) {
      const selected = data.segments[segment][view];
      return [selected.growers, selected.shrinks];
    }

    const selected = data[view];
    return [
      selected
        .filter((item) => item.value > 0)
        .sort((a, b) => b.value - a.value)
        .slice(0, 10),
      selected
        .filter((item) => item.value < 0)
        .sort((a, b) => a.value - b.value)
        .slice(0, 10),
    ];
  }, [data, isSegmented, segment, view]);

  return (
    <section className={styles.marketIntelSection} data-reveal="left">
      <MantineBox className={styles.marketIntelHeader}>
        <MantineBox>
          <MantineTitle order={2} className={styles.sectionTitleDark}>
            BIGGEST MOVEMENTS
          </MantineTitle>
          <MantineText className={styles.sectionTitleDarkSub}>{subtitle}</MantineText>
        </MantineBox>
        <MantineBox className={styles.miToggle}>
          {(["price", "volume"] as const).map((option) => (
            <MantineUnstyledButton
              key={option}
              className={`${styles.miToggleBtn} ${view === option ? styles.miToggleBtnActive : ""}`}
              onClick={() => setView(option)}
              aria-pressed={view === option}
            >
              {option === "price" ? "PRICE CHANGE" : "VOLUME CHANGE"}
            </MantineUnstyledButton>
          ))}
        </MantineBox>
        {isSegmented && (
          <MantineBox className={styles.miSegmentTabs}>
            <MantineBox className={styles.miToggle}>
              {(["clothing", "bags"] as const).map((option) => (
                <MantineUnstyledButton
                  key={option}
                  className={`${styles.miToggleBtn} ${segment === option ? styles.miToggleBtnActive : ""}`}
                  onClick={() => setSegment(option)}
                  aria-pressed={segment === option}
                >
                  {option.toUpperCase()}
                </MantineUnstyledButton>
              ))}
            </MantineBox>
          </MantineBox>
        )}
      </MantineBox>
      <MantineBox className={styles.miGrid}>
        {(["GROWERS", "SHRINKERS"] as const).map((title, columnIndex) => (
          <MantineBox key={title}>
            <MantineText className={styles.miColTitle}>{title}</MantineText>
            <MantineTable className={styles.marketIntelTable}>
              <MantineTable.Thead>
                <MantineTable.Tr>
                  <MantineTable.Th>#</MantineTable.Th>
                  <MantineTable.Th>BRAND</MantineTable.Th>
                  <MantineTable.Th>CHANGE</MantineTable.Th>
                </MantineTable.Tr>
              </MantineTable.Thead>
              <MantineTable.Tbody>
                {columns[columnIndex].slice(0, 10).map((item, index) => (
                  <MantineTable.Tr key={item.label}>
                    <MantineTable.Td className={styles.miRank}>
                      {isSegmented ? item.rank : index + 1}
                    </MantineTable.Td>
                    <MantineTable.Td className={styles.miBrand}>{item.label}</MantineTable.Td>
                    <MantineTable.Td
                      className={item.value > 0 ? styles.miPositive : styles.miNegative}
                    >
                      {item.value > 0 ? "+" : ""}
                      {item.value}%
                    </MantineTable.Td>
                  </MantineTable.Tr>
                ))}
              </MantineTable.Tbody>
            </MantineTable>
          </MantineBox>
        ))}
      </MantineBox>
      <MantineText span className={`${styles.tableNote} ${styles.tableNoteDark}`}>
        {note}
      </MantineText>
    </section>
  );
}

export function TopVendorsSection({
  data,
  categories,
  initialBrand,
}: {
  data: TopVendorsData;
  categories: string[];
  initialBrand: string;
}) {
  const brands = useMemo(() => getBrands(data.brands, initialBrand), [data.brands, initialBrand]);
  const [brand, setBrand] = useState(initialBrand);
  const [category, setCategory] = useState(categories[0]);

  return (
    <section className={`${styles.gradientSection} ${styles.topVendorsSection}`} data-reveal="left">
      <MantineBox className={styles.sectionHeader}>
        <MantineTitle order={2} className={styles.sectionTitle}>
          TOP VENDORS
        </MantineTitle>
        <MantineText className={styles.sectionTitleItalicSub}>
          Largest product volume for brands and categories.
        </MantineText>
      </MantineBox>
      <MantineBox className={styles.bubbleControls}>
        <Select
          className={styles.filterSelect}
          value={brand}
          onChange={(value) => setBrand(value ?? initialBrand)}
          data={brands}
          size="xs"
          radius="md"
          aria-label="Top vendors brand"
        />
        <Select
          className={styles.filterSelect}
          value={category}
          onChange={(value) => setCategory(value ?? categories[0])}
          data={categories}
          size="xs"
          radius="md"
          aria-label="Top vendors category"
        />
      </MantineBox>
      <VendorTable data={data.byBrand[brand]?.[category] ?? EMPTY_VENDOR_GROUPS} />
      <MantineText span className={styles.tableNote}>
        Ranked by deduplicated listing count — identical brand/vendor/price combinations counted
        once. Reflects inventory breadth, not sales velocity.
      </MantineText>
    </section>
  );
}

export function BestDealsSection({
  data,
  brands: sourceBrands,
  categories,
  initialBrand,
}: {
  data: LowestMedianData;
  brands: string[];
  categories: string[];
  initialBrand: string;
}) {
  const brands = useMemo(() => getBrands(sourceBrands, initialBrand), [initialBrand, sourceBrands]);
  const [brand, setBrand] = useState(initialBrand);
  const [category, setCategory] = useState(categories[0]);

  return (
    <section className={styles.gradientSection} data-reveal="right">
      <MantineBox className={styles.sectionHeader}>
        <MantineTitle order={2} className={styles.sectionTitle}>
          BEST DEALS
        </MantineTitle>
        <MantineText className={styles.sectionTitleItalicSub}>
          Lowest median prices across tracked platforms.
        </MantineText>
      </MantineBox>
      <MantineBox className={styles.bubbleControls}>
        <Select
          value={brand}
          onChange={(value) => setBrand(value ?? initialBrand)}
          data={brands}
          size="xs"
          radius="md"
          aria-label="Best deals brand"
        />
        <Select
          value={category}
          onChange={(value) => setCategory(value ?? categories[0])}
          data={categories}
          size="xs"
          radius="md"
          aria-label="Best deals category"
        />
      </MantineBox>
      <PriceVendorTable data={data.byBrand[brand]?.[category] ?? EMPTY_VENDOR_GROUPS} />
      <MantineText span className={styles.tableNote}>
        Listings priced below $5 or above $50,000, and those priced below 10% of their brand&apos;s
        median, are excluded as likely data errors. Minimum 5 listings required per vendor/category.
        Dropshipper placeholders excluded.
      </MantineText>
    </section>
  );
}

export function DesignerAtlasSection({
  mapData,
  highestQuantityData,
  medianPriceData,
  brands: sourceBrands,
  initialBrand,
}: {
  mapData: ReportMapData;
  highestQuantityData: HighestQuantityData;
  medianPriceData: MedianPriceData;
  brands: string[];
  initialBrand: string;
}) {
  const brands = useMemo(() => getBrands(sourceBrands, initialBrand), [initialBrand, sourceBrands]);
  const [brand, setBrand] = useState(initialBrand);
  const [category, setCategory] = useState(mapData.categories[0]);
  const [mode, setMode] = useState<"quantity" | "medianPrice">("quantity");
  const [view, setView] = useState<"map" | "table">(() =>
    typeof window !== "undefined" && window.innerWidth < 850 ? "table" : "map"
  );

  return (
    <section className={styles.mapSection} data-reveal="up">
      <MantineTitle order={2} className={`${styles.sectionTitleDark} ${styles.atlasTitle}`}>
        DESIGNER ATLAS
      </MantineTitle>
      <MantineBox className={styles.mapControls}>
        <MantineBox className={styles.mapModeToggle}>
          {(["map", "table"] as const).map((option) => (
            <MantineUnstyledButton
              key={option}
              className={`${styles.modeBtn} ${view === option ? styles.modeBtnActive : ""}`}
              onClick={() => setView(option)}
              aria-pressed={view === option}
            >
              {option.toUpperCase()}
            </MantineUnstyledButton>
          ))}
        </MantineBox>
        {view === "map" && (
          <MantineBox className={styles.mapModeToggle}>
            {(["quantity", "medianPrice"] as const).map((option) => (
              <MantineUnstyledButton
                key={option}
                className={`${styles.modeBtn} ${mode === option ? styles.modeBtnActive : ""}`}
                onClick={() => setMode(option)}
                aria-pressed={mode === option}
              >
                {option === "quantity" ? "LISTING VOLUME" : "MEDIAN PRICE"}
              </MantineUnstyledButton>
            ))}
          </MantineBox>
        )}
        <Select
          value={brand}
          onChange={(value) => setBrand(value ?? initialBrand)}
          data={brands}
          size="xs"
          radius="md"
          aria-label="Designer atlas brand"
        />
        <Select
          value={category}
          onChange={(value) => setCategory(value ?? mapData.categories[0])}
          data={mapData.categories}
          size="xs"
          radius="md"
          aria-label="Designer atlas category"
        />
      </MantineBox>
      {view === "map" ? (
        <AnalyticsMap data={mapData.byBrand[brand]?.[category] ?? {}} mode={mode} />
      ) : (
        <MantineBox className={styles.mapTablePanel}>
          <MantineBox className={styles.mapTableGrid}>
            <CountryTable
              title="HIGHEST VOLUME"
              valueLabel="LISTINGS"
              rows={(highestQuantityData.byBrand[brand]?.[category] ?? [])
                .slice(0, 10)
                .map((row) => ({
                  rank: row.rank,
                  country: row.country,
                  value: formatNumber(row.count),
                }))}
            />
            <CountryTable
              title="BEST DEALS"
              valueLabel="MED. PRICE"
              rows={(medianPriceData.byBrand[brand]?.[category] ?? []).slice(0, 10).map((row) => ({
                rank: row.rank,
                country: row.country,
                value: `$${row.medianPrice.toLocaleString()}`,
              }))}
            />
          </MantineBox>
          <MantineText span className={`${styles.tableNote} ${styles.tableNoteDark}`}>
            Country reflects seller location. Median price derived from cleaned listings ($5–$50,000
            range). Minimum 5 listings required per country/category to appear.
          </MantineText>
        </MantineBox>
      )}
    </section>
  );
}

function CountryTable({
  title,
  valueLabel,
  rows,
}: {
  title: string;
  valueLabel: string;
  rows: Array<{ rank: number; country: string; value: string }>;
}) {
  return (
    <MantineBox>
      <MantineText className={styles.miColTitle}>{title}</MantineText>
      <MantineTable className={styles.marketIntelTable}>
        <MantineTable.Thead>
          <MantineTable.Tr>
            <MantineTable.Th>#</MantineTable.Th>
            <MantineTable.Th>COUNTRY</MantineTable.Th>
            <MantineTable.Th>{valueLabel}</MantineTable.Th>
          </MantineTable.Tr>
        </MantineTable.Thead>
        <MantineTable.Tbody>
          {rows.map((row) => (
            <MantineTable.Tr key={row.rank}>
              <MantineTable.Td className={styles.miRank}>{row.rank}</MantineTable.Td>
              <MantineTable.Td className={styles.miBrand}>
                {countryName(row.country)}
              </MantineTable.Td>
              <MantineTable.Td>{row.value}</MantineTable.Td>
            </MantineTable.Tr>
          ))}
        </MantineTable.Tbody>
      </MantineTable>
    </MantineBox>
  );
}

export function VendorIntelligenceSection({
  data,
  brands: sourceBrands,
  categories,
  children,
}: {
  data: VendorBubbleData;
  brands: string[];
  categories: string[];
  children: ReactNode;
}) {
  const brands = useMemo(() => getBrands(sourceBrands, "All Brands"), [sourceBrands]);
  const selectableCategories = useMemo(
    () => ["All Categories", ...categories.filter((category) => category !== "All Categories")],
    [categories]
  );
  const [brand, setBrand] = useState("All Brands");
  const [category, setCategory] = useState("All Categories");

  return (
    <section className={styles.bubbleSection} data-reveal="up">
      <MantineTitle order={2} className={styles.sectionTitleDark}>
        VENDOR INTELLIGENCE
        <br />
        <MantineText span className={styles.sectionTitleSubBlack}>
          VOLUME · PRICE · RANGE
        </MantineText>
      </MantineTitle>
      <MantineBox className={styles.bubbleControls}>
        <Select
          value={brand}
          onChange={(value) => setBrand(value ?? "All Brands")}
          data={brands}
          size="xs"
          radius="md"
          aria-label="Vendor intelligence brand"
        />
        <Select
          value={category}
          onChange={(value) => setCategory(value ?? "All Categories")}
          data={selectableCategories}
          size="xs"
          radius="md"
          aria-label="Vendor intelligence category"
        />
      </MantineBox>
      <MantineBox className={styles.bubbleGrid}>
        {(
          [
            { label: "MARKETPLACES", values: data.marketplace[brand]?.[category] ?? [] },
            { label: "INDEPENDENT STORES", values: data.independent[brand]?.[category] ?? [] },
          ] as const
        ).map(({ label, values }) => (
          <MantineBox key={label} className={styles.bubblePanel}>
            <MantineText span className={styles.radarPanelTitle}>
              {label}
            </MantineText>
            <ResponsiveContainer width="100%" height={380}>
              <ScatterChart margin={{ top: 20, right: 20, bottom: 40, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis
                  type="number"
                  dataKey="x"
                  name="Volume"
                  tick={{ fontSize: 10 }}
                  tickFormatter={(value) =>
                    value >= 1000 ? `${(value / 1000).toFixed(0)}k` : String(value)
                  }
                  label={{
                    value: "LISTING VOLUME",
                    position: "insideBottom",
                    offset: -20,
                    fontSize: 9,
                    letterSpacing: "0.1em",
                    fill: "#999",
                  }}
                />
                <YAxis
                  type="number"
                  dataKey="y"
                  name="Median Price"
                  tick={{ fontSize: 10 }}
                  tickFormatter={(value) =>
                    `$${value >= 1000 ? `${(value / 1000).toFixed(0)}k` : String(value)}`
                  }
                  label={{
                    value: "MEDIAN PRICE",
                    angle: -90,
                    position: "insideLeft",
                    offset: 15,
                    fontSize: 9,
                    letterSpacing: "0.1em",
                    fill: "#999",
                  }}
                />
                <ZAxis type="number" dataKey="z" range={[40, 500]} name="IQR" />
                <Tooltip
                  content={<BubbleTooltip />}
                  animationDuration={0}
                  wrapperStyle={{ transition: "none" }}
                />
                <Scatter data={values} fillOpacity={0.8}>
                  {values.map((value, index) => (
                    <Cell
                      key={`${value.name}-${index}`}
                      fill={BUBBLE_COLORS[index % BUBBLE_COLORS.length]}
                    />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </MantineBox>
        ))}
      </MantineBox>
      {children}
    </section>
  );
}

export function PriceDistributionSection({
  data,
  brands: sourceBrands,
  children,
}: {
  data: PriceDistributionData;
  brands: string[];
  children: ReactNode;
}) {
  const brands = useMemo(() => getBrands(sourceBrands, "All Brands"), [sourceBrands]);
  const [brand, setBrand] = useState("All Brands");

  return (
    <section className={styles.violinSection} data-reveal="left">
      <MantineTitle order={2} className={styles.sectionTitleDark}>
        PRICE DISTRIBUTION
        <br />
        <MantineText span className={styles.sectionTitleSubBlack}>
          BY BRAND &amp; VENDOR
        </MantineText>
      </MantineTitle>
      <MantineBox className={styles.bubbleControls}>
        <Select
          value={brand}
          onChange={(value) => setBrand(value ?? "All Brands")}
          data={brands}
          size="xs"
          radius="md"
          aria-label="Price distribution brand"
        />
      </MantineBox>
      <MantineBox className={styles.violinGrid}>
        {(["marketplace", "independent"] as const).map((type) => {
          const violinData = getViolinData(data[type], brand);
          return (
            <MantineBox key={type} className={styles.violinPanel}>
              <MantineBox className={styles.violinPanelHeader}>
                <MantineText span className={styles.radarPanelTitle}>
                  {type === "independent" ? "INDEPENDENT STORES" : "MARKETPLACES"}
                </MantineText>
              </MantineBox>
              {violinData.length === 0 ? (
                <MantineText className={styles.noDataMessage}>
                  No data available for this selection.
                </MantineText>
              ) : (
                <ViolinChart
                  key={`${type}-${brand}`}
                  data={violinData}
                  color={type === "independent" ? "#1500ff" : "#6600aa"}
                />
              )}
            </MantineBox>
          );
        })}
      </MantineBox>
      {children}
    </section>
  );
}

export function ArbitrageSection({
  data,
  brands: sourceBrands,
  categories,
  initialBrand,
}: {
  data: ArbitrageWindowsData;
  brands: string[];
  categories: string[];
  initialBrand: string;
}) {
  const brands = useMemo(() => getBrands(sourceBrands), [sourceBrands]);
  const [brand, setBrand] = useState(initialBrand);
  const [category, setCategory] = useState(categories[0]);
  const rows = getArbitrageData(data.byBrand, brand, category);

  return (
    <section className={styles.gradientSection} data-reveal="right">
      <MantineBox className={styles.sectionHeader}>
        <MantineTitle order={2} className={styles.sectionTitle}>
          ARBITRAGE WINDOWS
        </MantineTitle>
        <MantineText className={styles.sectionTitleItalicSub}>
          Biggest median price gaps between platforms — buy low, sell high
        </MantineText>
      </MantineBox>
      <MantineBox className={styles.bubbleControls}>
        <Select
          value={brand}
          onChange={(value) => setBrand(value ?? initialBrand)}
          data={brands}
          size="xs"
          radius="md"
          aria-label="Arbitrage brand"
        />
        <Select
          value={category}
          onChange={(value) => setCategory(value ?? categories[0])}
          data={categories}
          size="xs"
          radius="md"
          aria-label="Arbitrage category"
        />
      </MantineBox>
      <MantineBox className={styles.arbitrageTableWrapper}>
        <MantineTable className={styles.arbitrageTable}>
          <MantineTable.Thead>
            <MantineTable.Tr>
              <MantineTable.Th>#</MantineTable.Th>
              <MantineTable.Th>BUY ON</MantineTable.Th>
              <MantineTable.Th>BUY PRICE</MantineTable.Th>
              <MantineTable.Th aria-label="Direction" />
              <MantineTable.Th>SELL ON</MantineTable.Th>
              <MantineTable.Th>SELL PRICE</MantineTable.Th>
              <MantineTable.Th>SPREAD</MantineTable.Th>
            </MantineTable.Tr>
          </MantineTable.Thead>
          <MantineTable.Tbody>
            {rows.map((row) => (
              <MantineTable.Tr key={row.rank}>
                <MantineTable.Td className={styles.hvRank}>{row.rank}</MantineTable.Td>
                <MantineTable.Td className={styles.arbMarket}>{row.buyMarket}</MantineTable.Td>
                <MantineTable.Td className={styles.arbPrice}>
                  ${row.buyPrice.toLocaleString()}
                </MantineTable.Td>
                <MantineTable.Td className={styles.arbArrow}>→</MantineTable.Td>
                <MantineTable.Td className={styles.arbMarket}>{row.sellMarket}</MantineTable.Td>
                <MantineTable.Td className={styles.arbPrice}>
                  ${row.sellPrice.toLocaleString()}
                </MantineTable.Td>
                <MantineTable.Td className={styles.arbSpread}>
                  +${row.spread.toLocaleString()}{" "}
                  <MantineText span className={styles.arbSpreadPct}>
                    (+{row.spreadPct}%)
                  </MantineText>
                </MantineTable.Td>
              </MantineTable.Tr>
            ))}
          </MantineTable.Tbody>
        </MantineTable>
      </MantineBox>
      <MantineText span className={styles.tableNote}>
        Marketplace platforms only. Within-group IQR filtering applied per vendor to remove
        outliers. Minimum 10 listings required per platform/category. Spread reflects median price
        differential — does not account for platform fees, shipping, or authentication costs.
      </MantineText>
    </section>
  );
}
